/**
 * Bundle Matching Module
 * Handles automatic equipment bundle recommendation based on device selection
 * Supports MRB bundles with various DSP models, microphone types, and speaker models
 */

import { find, filter } from 'lodash';
import equipmentCatalog from 'globalConstants/EquipmentCatalog';

/**
 * UC Compute models that require special bundle handling
 */
const UC_COMPUTE_MODELS = ['VBC 2800'];

/**
 * Map of speaker model names to their bundle codes
 */
const SPEAKER_BUNDLE_CODES = {
  'Desono C-IC6 (units)': 'CIC6',
  'Desono C-IC6': 'CIC6', // Legacy name support
  'Desono C-IC6 LP': 'CIC6LP',
  'Desono C-IC6 LP-TAA': 'CIC6LP-TAA',
  'Desono C-IC6LP': 'CIC6LP',
  'Desono C-IC6LP (units)': 'CIC6LP',
  'Desono C-IC6LP-TAA': 'CIC6LP-TAA', // Alternative format
  'Desono C-IC6LP-TAA (units)': 'CIC6LP-TAA',
  'Parlé P6-SM': 'P6SM',
  'Desono P6-SM': 'P6SM',
  'Desono P6-SM (units)': 'P6SM',
  'Desono DXS5': 'DXS5',
  'Desono DX-S5': 'DXS5',
  'Desono DX-S5 (units)': 'DXS5',
  'Community ENT206': 'ENT206',
  'Desono ENT206': 'ENT206',
  'Desono EXS8': 'EXS8',
  'Desono EX-S8': 'EXS8',
  'Desono EX-S8 (units)': 'EXS8',
  'Community MASK6C': 'MASK6C',
  'Desono MASK6C': 'MASK6C',
  'Desono MASK6C (units)': 'MASK6C',
  'TesiraSCIP-T': 'TSC2DIP-T',
  'TesiraSCIP-Z': 'TSC2DIP-Z',
};

/**
 * Bundle size definitions
 */
const BUNDLE_SIZES = {
  MEDIUM: { code: 'M', maxParticipants: 11, micQty: 1, spkrQty: 2 },
  LARGE: { code: 'L', maxParticipants: 18, micQty: 2, spkrQty: 4 }
};

/**
 * DSP model mapping for bundles
 */
const DSP_MODELS = {
  'Tesira': 'X400',
  'Devio': 'SCX400'
};

/**
 * Determines if UC Compute is required based on selected devices
 * @param {Object} devices - Object containing selected devices
 * @returns {boolean} True if UC Compute is required
 */
export const requiresUCCompute = (devices) => {
  // Check if any device requires UC compute (e.g., certain camera models)
  const { cameras = {} } = devices;
  
  // Add logic here if certain camera models require UC compute
  // For now, return false unless explicitly set
  return devices.requiresUCCompute === true;
};

/**
 * Gets the speaker bundle code from model name
 * @param {string} speakerModel - Speaker model name
 * @returns {string|null} Bundle code or null if not found
 */
const getSpeakerBundleCode = (speakerModel) => {
  // First check if speaker model has bundleCode property
  const speaker = find(equipmentCatalog.Speakers, s => s.modelName === speakerModel);
  if (speaker?.bundleCode) {
    return speaker.bundleCode;
  }
  
  // Fallback to mapping
  return SPEAKER_BUNDLE_CODES[speakerModel] || null;
};

/**
 * Determines the appropriate bundle size based on quantities
 * @param {number} micQty - Number of microphones
 * @param {number} spkrQty - Number of speakers
 * @returns {Object|null} Bundle size configuration or null if no match
 */
const getBundleSize = (micQty, spkrQty) => {
  // Check if quantities match medium bundle
  if (micQty <= BUNDLE_SIZES.MEDIUM.micQty && spkrQty <= BUNDLE_SIZES.MEDIUM.spkrQty) {
    return BUNDLE_SIZES.MEDIUM;
  }
  
  // Check if quantities match large bundle
  if (micQty <= BUNDLE_SIZES.LARGE.micQty && spkrQty <= BUNDLE_SIZES.LARGE.spkrQty) {
    return BUNDLE_SIZES.LARGE;
  }
  
  // Quantities exceed bundle limits
  return null;
};

/**
 * Generates bundle model name based on configuration
 * @param {Object} config - Bundle configuration
 * @returns {string} Bundle model name
 */
const generateBundleModelName = (config) => {
  const { size, dspModel, micType, speakerCode, ucCompute, productFamily } = config;

  // Format: MRB-[Size][DSP/UC][MicType]-[SpeakerCode]
  // Examples:
  // - MRB-M-X400-C-CIC6 (Medium, Tesira X400, Ceiling mic, C-IC6 speaker)
  // - MRB-M-SCX400-C (Medium, Devio SCX400, Ceiling mic - CIC6 speakers only, no suffix)
  // - MRB-L-SCX400-T (Large, Devio SCX400, Tabletop mic - CIC6 speakers only, no suffix)
  // - MRB-M-VBC2800-C-CIC6LP (Medium, VBC 2800 UC compute, Ceiling mic, C-IC6 LP speaker)

  const dspPart = ucCompute ? 'VBC2800' : dspModel;

  // Devio bundles only support CIC6 speakers and don't include speaker code in the name
  if (productFamily === 'Devio' && dspModel === 'SCX400') {
    return `Biamp MRB-${size}-${dspPart}-${micType}`;
  }

  return `Biamp MRB-${size}-${dspPart}-${micType}-${speakerCode}`;
};

/**
 * Finds matching bundle from catalog
 * @param {string} modelName - Generated bundle model name
 * @returns {Object|null} Bundle object from catalog or null if not found
 */
const findBundleInCatalog = (modelName) => {
  return find(equipmentCatalog.Bundles, bundle => bundle.modelName === modelName);
};

/**
 * Creates a dynamic bundle definition if not in catalog
 * @param {Object} config - Bundle configuration
 * @returns {Object} Bundle object
 */
const createDynamicBundle = (config) => {
  const { size, productFamily, micModel, spkrModel, ucCompute } = config;
  const bundleSize = size === 'M' ? BUNDLE_SIZES.MEDIUM : BUNDLE_SIZES.LARGE;
  
  const modelName = generateBundleModelName(config);
  const description = ucCompute 
    ? `UC Compute meeting room bundle for ${size === 'M' ? 'medium' : 'large'} rooms (up to ${bundleSize.maxParticipants} participants).`
    : `Certified meeting room bundle for ${size === 'M' ? 'medium' : 'large'} rooms (up to ${bundleSize.maxParticipants} participants).`;
  
  return {
    modelName,
    partNumber: `950.XXXX.900`, // Dynamic part number
    description,
    productFamily,
    requiresUCCompute: ucCompute,
    includes: {
      micModel,
      micQty: bundleSize.micQty,
      spkrModel,
      spkrQty: bundleSize.spkrQty,
      dsp: ucCompute ? 'VBC 2800' : (productFamily === 'Tesira' ? 'TesiraFORTÉ X 400' : 'Devio SCX 400')
    }
  };
};

/**
 * Main bundle matching function
 * @param {Object} params - Bundle matching parameters
 * @param {string} params.productFamily - Product family (Tesira/Devio)
 * @param {string} params.micModel - Microphone model name
 * @param {number} params.micQty - Number of microphones
 * @param {string} params.spkrModel - Speaker model name
 * @param {number} params.spkrQty - Number of speakers
 * @param {boolean} params.requiresUCCompute - Whether UC compute is required
 * @returns {Object} Bundle matching result
 */
export const matchBundle = (params) => {
  const { 
    productFamily, 
    micModel, 
    micQty, 
    spkrModel, 
    spkrQty,
    requiresUCCompute = false
  } = params;
  
  // Validate inputs
  if (!productFamily || !micModel || !spkrModel) {
    return {
      bundle: null,
      reason: 'Missing required parameters',
      fallbackToDSP: true
    };
  }
  
  // Check quantity limits for bundles
  if (micQty > 4 || spkrQty > 16) {
    return {
      bundle: null,
      reason: 'Quantities exceed bundle limits',
      fallbackToDSP: true,
      excessMics: micQty > 4 ? micQty - 4 : 0,
      excessSpeakers: spkrQty > 16 ? spkrQty - 16 : 0
    };
  }
  
  // Select bundle size based on microphone count. Speaker quantities beyond
  // bundle capacity, and mic quantities above LARGE.micQty (up to the
  // per-bundle cap enforced at the top of this function), are surfaced as
  // excess and added a-la-carte via calculateAdditionalEquipment.
  const bundleSize = micQty <= BUNDLE_SIZES.MEDIUM.micQty
    ? BUNDLE_SIZES.MEDIUM
    : BUNDLE_SIZES.LARGE;
  
  // Determine microphone type (C for ceiling, T for tabletop)
  const mic = find(equipmentCatalog.Microphones, m =>
    m.modelName && (m.modelName === micModel || m.modelName.includes(micModel))
  );
  const micType = mic?.isTableTop ? 'T' : 'C';

  // PIG-595 (spec §3.1 "Bundle vs Individual DSP Decision Tree"): a ceiling design
  // with C-IC6 speakers only benefits from a bundle's integrated head amp when the
  // speaker count uses it efficiently. `useHeadAmp` is true for 1–4, 9–12, …
  // speakers (a partial group of ≤4 the head amp covers) and false for 5–8, 13–16,
  // … where a standalone AMP-450BP is the cheaper cover for ALL speakers. When it's
  // false the bundle's head amp would be paid for and then duplicated by an external
  // amp for the remainder (the reported bug), so return no bundle and let the design
  // fall through to the à-la-carte DSP path.
  //   - Tabletop mics have no head amp — the cost heuristic doesn't apply, so they
  //     keep matching by mic count as before.
  //   - P6-SM pendant speakers keep their bundle + overflow amp (Case C, PIG-534)
  //     and wall speakers have their own amp path, so the gate is C-IC6-only.
  //   - Guarded on `spkrQty > 0` so mic-only rooms are unaffected.
  const isCIC6 = typeof spkrModel === 'string' && spkrModel.includes('C-IC6');
  const useHeadAmp = spkrQty % 8 > 0 && spkrQty % 8 <= 4;
  if (micType === 'C' && isCIC6 && spkrQty > 0 && !useHeadAmp) {
    return {
      bundle: null,
      reason: `Speaker count (${spkrQty}) is more cost-effective à la carte with a standalone AMP-450BP than a head-amp bundle`,
      fallbackToDSP: true,
    };
  }

  // Get speaker bundle code
  const speakerCode = getSpeakerBundleCode(spkrModel);
  if (!speakerCode) {
    return {
      bundle: null,
      reason: `Speaker model ${spkrModel} not supported in bundles`,
      fallbackToDSP: true
    };
  }

  // Devio bundles only support CIC6 speakers
  if (productFamily === 'Devio' && speakerCode !== 'CIC6') {
    return {
      bundle: null,
      reason: `Devio bundles only support CIC6 speakers. Selected speaker: ${spkrModel}`,
      fallbackToDSP: true
    };
  }

  // Determine DSP model
  const dspModel = requiresUCCompute ? 'VBC2800' : DSP_MODELS[productFamily];
  if (!dspModel && !requiresUCCompute) {
    return {
      bundle: null,
      reason: `Product family ${productFamily} not supported`,
      fallbackToDSP: true
    };
  }
  
  // Create bundle configuration
  const bundleConfig = {
    size: bundleSize.code,
    dspModel,
    micType,
    speakerCode,
    ucCompute: requiresUCCompute,
    productFamily,
    micModel: mic?.modelName || micModel,
    spkrModel
  };
  
  // Generate bundle model name
  const bundleModelName = generateBundleModelName(bundleConfig);
  
  // Try to find bundle in catalog
  let bundle = findBundleInCatalog(bundleModelName);
  
  // If not found in catalog, create dynamic bundle
  if (!bundle) {
    bundle = createDynamicBundle(bundleConfig);
  }
  
  // Calculate excess quantities
  const excessMics = Math.max(0, micQty - bundleSize.micQty);
  const excessSpeakers = Math.max(0, spkrQty - bundleSize.spkrQty);
  
  return {
    bundle,
    excessMics,
    excessSpeakers,
    bundleConfig,
    fallbackToDSP: false
  };
};

/**
 * Calculates additional equipment needed beyond bundle
 * @param {Object} bundle - Selected bundle
 * @param {number} excessMics - Excess microphones beyond bundle
 * @param {number} excessSpeakers - Excess speakers beyond bundle
 * @returns {Array} Array of additional equipment items
 */
export const calculateAdditionalEquipment = (bundle, excessMics, excessSpeakers) => {
  const additionalEquipment = [];
  
  // Add excess microphones
  if (excessMics > 0) {
    const micModel = bundle.includes.micModel;
    const mic = find(equipmentCatalog.Microphones, m => 
      m.modelName && m.modelName.includes(micModel)
    );
    
    if (mic) {
      // Determine if we need base units, amplified units, or expanders
      if (mic.isExpanderUnit) {
        additionalEquipment.push({
          ...mic,
          qty: excessMics,
          reason: 'Expansion microphones beyond bundle'
        });
      } else {
        // For base units, might need amplified versions
        const ampMic = find(equipmentCatalog.Microphones, m => 
          m.modelName && m.modelName.includes(micModel) && m.isAmpUnit
        );
        if (ampMic && excessMics > 2) {
          // Use amplified unit for larger quantities
          additionalEquipment.push({
            ...ampMic,
            qty: 1,
            reason: 'Amplified microphone unit for expansion'
          });
          if (excessMics > 1) {
            additionalEquipment.push({
              ...mic,
              qty: excessMics - 1,
              reason: 'Additional microphones beyond bundle'
            });
          }
        } else {
          additionalEquipment.push({
            ...mic,
            qty: excessMics,
            reason: 'Additional microphones beyond bundle'
          });
        }
      }
    }
  }
  
  // Add excess speakers
  if (excessSpeakers > 0) {
    const spkrModel = bundle.includes.spkrModel;
    // Try to find speaker by exact match first, then by partial match
    let speaker = find(equipmentCatalog.Speakers, s => 
      s.modelName === spkrModel
    );
    
    // If not found by exact match, try partial match
    if (!speaker) {
      speaker = find(equipmentCatalog.Speakers, s => 
        s.modelName && spkrModel && s.modelName.includes(spkrModel)
      );
    }
    
    // If still not found, try by bundle code
    if (!speaker && spkrModel) {
      const bundleCode = SPEAKER_BUNDLE_CODES[spkrModel] || spkrModel.replace(/-/g, '');
      speaker = find(equipmentCatalog.Speakers, s => 
        s.bundleCode === bundleCode || (s.modelName && s.modelName.includes(bundleCode))
      );
    }
    
    if (speaker) {
      additionalEquipment.push({
        ...speaker,
        qty: excessSpeakers,
        reason: 'Additional speakers beyond bundle'
      });
    }
  }
  
  return additionalEquipment;
};

/**
 * Optimizes bundle selection based on cost and coverage
 * @param {Array} possibleBundles - Array of possible bundle configurations
 * @returns {Object} Optimal bundle configuration
 */
export const optimizeBundleSelection = (possibleBundles) => {
  if (!possibleBundles || possibleBundles.length === 0) {
    return null;
  }
  
  // Sort by:
  // 1. Least excess equipment needed
  // 2. Best coverage match
  // 3. Cost optimization (if cost data available)
  
  return possibleBundles.sort((a, b) => {
    const aExcess = (a.excessMics || 0) + (a.excessSpeakers || 0);
    const bExcess = (b.excessMics || 0) + (b.excessSpeakers || 0);
    
    if (aExcess !== bExcess) {
      return aExcess - bExcess; // Less excess is better
    }
    
    // Additional sorting criteria can be added here
    return 0;
  })[0];
};